/**
 * "Now & Next" — the Reservations tab's DEFAULT landing view (DNKADM-0271,
 * rebuilt for the front-counter tablet by DNKADM-0298 to the approved
 * ReservationsBoard/Reservations-Tablet design): a task-first board for the
 * front desk's minute-to-minute job, built ENTIRELY from the already-loaded
 * day window + rosters (no new endpoints).
 *
 * Layout (DNKADM-0298 U3 — the design's collapse order, exactly):
 *  - **Desktop / tablet landscape (md+, ≥900px):** the three sections on the
 *    left, a fixed 292px right rail holding the coach-load chart (vertical
 *    bars), the "Room for a walk-in" panel, and the day counters.
 *  - **Tablet portrait (<md):** the rail leaves the side and becomes a single
 *    compact strip rendered under the header, ABOVE the sections (the
 *    design's own collapse note: "a single strip under the header") — same
 *    three facts, coach load as horizontal bars. Later-today rows stay one
 *    line; nothing else shrinks. Touch targets are ≥48px and NOTHING depends
 *    on hover (hover styles are decoration only).
 *
 * Sections (bucketing rules in `nowNextBuckets.ts` — unchanged by 0298):
 *  - **Arriving now & soon** — CONFIRMED, not-yet-checked-in reservations in
 *    progress / starting within {@link SOON_WINDOW_MINUTES} / lingering
 *    post-end for a missed check-in. Inline **Check in** / **No-show**.
 *  - **Needs confirmation** — header count is TENANT-WIDE (DNKADM-0276
 *    semantics unchanged): `pendingTotal` floors to the day-window count;
 *    inline **Confirm** / **Decline**; the review-all link promotes when
 *    pending exists beyond today.
 *  - **Later today** — remaining confirmed reservations as ONE-LINE rows
 *    (the only group with no actions, so the first to lose detail).
 *
 * The rail content (DNKADM-0298 U1/U2) replaced the retrospective per-bay
 * occupancy strip — it measured the whole day and never referenced `now`.
 * Derivations live in `nowNextRail.ts` (pure, fixed-`now` tested): coach
 * load (one bar per ACTIVE-ROSTER coach, zero bars included; Done vs Still
 * to come split where in-progress counts as still to come) and walk-in
 * availability (free-now / free-until / open-all-day per bay; pending counts
 * as booked; unknown or out-of-hours operating windows claim nothing).
 *
 * Every card opens the existing `ReservationDrawer` via `onSelect`; the
 * `?reservation=<uuid>` deeplink keeps working because the drawer lives in
 * the shell (DNKADM-0147). Cancelled reservations never appear here. All
 * colors read theme tokens (DNKADM-0269 guard).
 */
import { useMemo, useState } from 'react';
import { Box, Button, Chip, Divider, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { Icon, resolveTokens, type AdminTokens } from '@/theme';
import { resolveCalendarMaps } from '@/theme/maps/calendar';
import type { Reservation } from '@/api/reservations';
import {
  derivePaymentState,
  deriveServiceColor,
  deriveStatusBucket,
  isPaymentInProgress,
  isPaymentNeedsReview,
  isUnpaid,
  paymentBadgeText,
  type HourRange,
  type PaymentState,
} from './derive';
import { zonedDateOf } from '@/components/list/listDate';
import { clockInZone, dateLabel } from '@/utils/clockTime';
import { BookingsListDialog } from './BookingsListDialog';
import { MemberNameLink } from '@/components/MemberNameLink';
import { serviceWithCoach } from '../coachLabel';
import {
  SOON_WINDOW_MINUTES,
  UNRESOLVED_WINDOW_DAYS,
  bucketReservations,
  isCheckedIn,
  minutesUntil,
  unresolvedReservations,
} from './nowNextBuckets';
import {
  coachLoad,
  dayProgress,
  walkInAvailability,
  type CoachLoadRow,
  type RosterEntry,
  type WalkInAvailability,
} from './nowNextRail';

export interface NowNextViewProps {
  /** The loaded day-window reservations (already location/filter-scoped). */
  reservations: Reservation[];
  /** The bay roster (id+label), or undefined to derive lanes from bookings. */
  roster: RosterEntry[] | undefined;
  /**
   * The ACTIVE staff roster (id+label) — the same rows the Grid's By-Staff
   * axis renders (DNKADM-0298 U1); undefined/empty hides the coach chart.
   */
  staffRoster: RosterEntry[] | undefined;
  /** The day's operating window (open→close hours), or null when unknown/closed. */
  operating: HourRange | null;
  /**
   * The TENANT-WIDE pending-confirmation total (DNKADM-0276) — the nav badge's
   * number (`usePendingConfirmationCount`), not a day-window derivation; null
   * until it loads (the header then falls back to the day-window count).
   * Inline Confirm/Decline stay day-window-scoped regardless — off-today
   * pending is reached via the review-all link, never actioned inline here.
   */
  pendingTotal: number | null;
  /** The venue IANA zone (fail-soft null — instants then use their own offset). */
  timeZone: string | null;
  /**
   * The unresolved-queue candidates (DNKADM-0350 U1) — CONFIRMED reservations
   * from the last {@link UNRESOLVED_WINDOW_DAYS} days, fetched by the shell
   * INDEPENDENTLY of the day window (a forgotten booking must stay visible no
   * matter what date the calendar is showing). Bucketed here via
   * `unresolvedReservations`, the same pure-function discipline as the day
   * window's `bucketReservations`. Defaults to `[]` (no unresolved section).
   */
  unresolvedCandidates?: Reservation[];
  /**
   * Whether this host lets the operator act on a card. When `false` the Check in /
   * No-show / Confirm / Decline buttons are ABSENT from every bucket — hidden, not
   * disabled — and the board itself is unchanged. Defaults to `true`. Since DNKADM-0375
   * (STAFF is front of house, DNKAPI-0532 opens these writes to STAFF) no production
   * caller passes `false`; the prop is kept for a view-only host.
   */
  canWrite?: boolean;
  /** The current instant — injectable for tests; defaults to `new Date()`. */
  now?: Date;
  /** Open the detail drawer on a card (the shell's `setSelectedBooking`). */
  onSelect: (r: Reservation) => void;
  /** The shell's existing action handlers (dialog routing included). */
  onCheckIn: (r: Reservation) => void;
  onNoShow: (r: Reservation) => void;
  onConfirm: (r: Reservation) => void;
  onDecline: (r: Reservation) => void;
}

/**
 * A short venue-clock time label for an ISO instant — 12-hour through the shared
 * formatter (DNKADM-0386 U2); the zone rule is unchanged (the venue zone, else device).
 */
function timeLabel(iso: string | null, timeZone: string | null): string {
  return clockInZone(iso, timeZone);
}

/** A stable render key for a reservation card (uuid preferred; degrade composite). */
function cardKey(r: Reservation): string {
  return r.reservationUuid ?? `${r.startTime ?? ''}|${r.assignedResourceId ?? ''}|${r.contactName ?? ''}`;
}

/** ≥48px touch target (DNKADM-0298 U3) — pressed standing up, not with a cursor. */
const TOUCH_BUTTON_SX = { minHeight: 48, px: 2.5, borderRadius: 2.5 } as const;

export function NowNextView({
  reservations,
  roster,
  staffRoster,
  operating,
  pendingTotal,
  timeZone,
  unresolvedCandidates = [],
  canWrite = true,
  now,
  onSelect,
  onCheckIn,
  onNoShow,
  onConfirm,
  onDecline,
}: NowNextViewProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const nowInstant = now ?? new Date();

  // `now ?? new Date()` is inlined (not `nowInstant`) so the deps arrays are
  // exactly the recompute triggers — the window refetch and the injected `now`;
  // the render-scope instant as a dep would re-derive every render.
  const buckets = useMemo(
    () => bucketReservations(reservations, now ?? new Date()),
    [reservations, now],
  );
  const unresolved = useMemo(
    () => unresolvedReservations(unresolvedCandidates, now ?? new Date()),
    [unresolvedCandidates, now],
  );
  const coaches = useMemo(
    () => coachLoad(reservations, staffRoster, now ?? new Date()),
    [reservations, staffRoster, now],
  );
  const walkIn = useMemo(
    () => walkInAvailability(reservations, roster, operating, timeZone, now ?? new Date()),
    [reservations, roster, operating, timeZone, now],
  );
  const progress = useMemo(
    () => dayProgress(reservations, now ?? new Date()),
    [reservations, now],
  );

  // Coach/bay booking popouts (DNKADM-0300 F): tapping a coach-load bar or a
  // walk-in bay row opens ONE shared bookings-list card (never a hover card);
  // its rows open the detail surface through the same `onSelect`.
  const [popout, setPopout] = useState<{ title: string; items: Reservation[] } | null>(null);
  const byStart = (a: Reservation, b: Reservation) =>
    (a.startTime ?? '').localeCompare(b.startTime ?? '');
  const openCoachPopout = (coach: CoachLoadRow) =>
    setPopout({
      title: `${coach.label} — today`,
      items: reservations
        .filter(
          (r) => deriveStatusBucket(r.status) !== 'cancelled' && r.assignedStaffId === coach.id,
        )
        .sort(byStart),
    });
  const openBayPopout = (bay: { id: string; label: string }) =>
    setPopout({
      title: `${bay.label} — today`,
      items: reservations
        .filter(
          (r) => deriveStatusBucket(r.status) !== 'cancelled' && r.assignedResourceId === bay.id,
        )
        .sort(byStart),
    });

  // Needs-confirmation header truth (DNKADM-0276, unchanged by 0298): the
  // tenant-wide total drives count and messaging; the day-window count is the
  // fallback while the total is unknown, and the FLOOR always.
  const todayPending = buckets.needsConfirmation.length;
  const pendingCount =
    pendingTotal === null ? todayPending : Math.max(pendingTotal, todayPending);
  const offTodayPending = Math.max(0, pendingCount - todayPending);

  const sections = (
    <Stack spacing={2}>
      <BoardSection
        label="Arriving now & soon"
        dotColor={t.success}
        empty="Nobody due in the next hour."
        count={buckets.arriving.length}
      >
        {buckets.arriving.map((r) => (
          <ReservationCard
            key={cardKey(r)}
            reservation={r}
            timeZone={timeZone}
            now={nowInstant}
            onSelect={onSelect}
            {...(isPaymentNeedsReview(r)
              ? { paymentState: 'needs-review' as const }
              : isUnpaid(r)
                ? { paymentState: 'unpaid' as const }
                : isPaymentInProgress(r)
                  ? { paymentState: 'in-progress' as const }
                  : {})}
            actions={
              canWrite ? (
                <>
                  <Button
                    variant="contained"
                    sx={{ ...TOUCH_BUTTON_SX, flex: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCheckIn(r);
                    }}
                  >
                    Check in
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    sx={TOUCH_BUTTON_SX}
                    onClick={(e) => {
                      e.stopPropagation();
                      onNoShow(r);
                    }}
                  >
                    No-show
                  </Button>
                </>
              ) : null
            }
          />
        ))}
      </BoardSection>

      <BoardSection
        label="Needs confirmation"
        dotColor={t.warn}
        // The tenant-wide-zero empty state is the only genuine "nothing to
        // confirm"; while the total is unknown the copy claims only the day.
        empty={
          pendingTotal === null
            ? 'No requests waiting on you today.'
            : 'No requests waiting on you.'
        }
        count={pendingCount}
        headerExtra={
          // The fine-print link is PROMOTED into the section body when pending
          // exists beyond today (DNKADM-0276) — one link, not two.
          offTodayPending > 0 ? undefined : (
            <MuiLink
              component={RouterLink}
              to="/reservations/pending"
              variant="body2"
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              All pending requests
            </MuiLink>
          )
        }
      >
        {buckets.needsConfirmation.map((r) => (
          <ReservationCard
            key={cardKey(r)}
            reservation={r}
            timeZone={timeZone}
            now={nowInstant}
            onSelect={onSelect}
            {...(isPaymentNeedsReview(r)
              ? { paymentState: 'needs-review' as const }
              : isUnpaid(r)
                ? { paymentState: 'unpaid' as const }
                : isPaymentInProgress(r)
                  ? { paymentState: 'in-progress' as const }
                  : {})}
            actions={
              canWrite ? (
                <>
                  <Button
                    variant="contained"
                    sx={{ ...TOUCH_BUTTON_SX, flex: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onConfirm(r);
                    }}
                  >
                    Confirm
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    sx={TOUCH_BUTTON_SX}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDecline(r);
                    }}
                  >
                    Decline
                  </Button>
                </>
              ) : null
            }
          />
        ))}
        {offTodayPending > 0 && (
          <MuiLink
            component={RouterLink}
            to="/reservations/pending"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              alignSelf: 'flex-start',
              fontSize: 13.5,
              fontWeight: 500,
              minHeight: 44,
            }}
          >
            {`${pendingCount} pending — review all requests`}
            <Icon name="arrow_forward" sx={{ fontSize: 16 }} />
          </MuiLink>
        )}
      </BoardSection>

      <BoardSection
        label="Later today"
        dotColor={t.textMuted}
        empty="Nothing else on the books today."
        count={buckets.later.length}
      >
        {buckets.later.map((r) => (
          <LaterRow
            key={cardKey(r)}
            reservation={r}
            timeZone={timeZone}
            onSelect={onSelect}
          />
        ))}
      </BoardSection>

      {/* Unresolved queue (DNKADM-0350 U1) — past CONFIRMED bookings nobody
          ever actioned, last {@link UNRESOLVED_WINDOW_DAYS} days. Rendered
          ONLY when non-empty (never permanent furniture on a clean day) —
          there is deliberately no zero-count empty-state card here. */}
      {unresolved.length > 0 && (
        <Paper elevation={1} sx={{ p: 2 }} component="section" aria-label="Unresolved">
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.warn }} />
              <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>
                Unresolved
              </Typography>
              <Chip
                size="small"
                label={unresolved.length}
                sx={{ height: 20, fontSize: 11.5, fontWeight: 600 }}
              />
            </Stack>
          </Stack>
          <Typography variant="body2" sx={{ mt: 0.25, color: t.text3 }}>
            {`Unresolved · last ${UNRESOLVED_WINDOW_DAYS} days`}
          </Typography>
          <Stack spacing={1.25} sx={{ mt: 1.25 }}>
            {unresolved.map((r) => (
              <ReservationCard
                key={cardKey(r)}
                reservation={r}
                timeZone={timeZone}
                now={nowInstant}
                onSelect={onSelect}
                paymentState={derivePaymentState(r)}
                actions={
                  canWrite ? (
                    <>
                      <Button
                        variant="contained"
                        sx={{ ...TOUCH_BUTTON_SX, flex: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onCheckIn(r);
                        }}
                      >
                        Check in
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        sx={TOUCH_BUTTON_SX}
                        onClick={(e) => {
                          e.stopPropagation();
                          onNoShow(r);
                        }}
                      >
                        No-show
                      </Button>
                    </>
                  ) : null
                }
              />
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );

  return (
    <Box
      data-testid="now-next-view"
      sx={{
        display: 'grid',
        gap: 2,
        alignItems: 'start',
        // Collapse step 1 (the design, exactly): the 292px rail sits beside
        // the sections at md+ (tablet landscape 1024 and desktop) and leaves
        // the side below md (tablet portrait 768), where the strip above
        // renders instead.
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 292px' },
      }}
    >
      <CompactRailStrip
        coaches={coaches}
        walkIn={walkIn}
        progress={progress}
        timeZone={timeZone}
        onCoachTap={openCoachPopout}
      />
      {sections}
      <SideRail
        coaches={coaches}
        walkIn={walkIn}
        progress={progress}
        timeZone={timeZone}
        onCoachTap={openCoachPopout}
        onBayTap={openBayPopout}
      />
      <BookingsListDialog
        open={popout !== null}
        title={popout?.title ?? ''}
        reservations={popout?.items ?? []}
        timeZone={timeZone}
        onClose={() => setPopout(null)}
        onSelect={(r) => {
          setPopout(null);
          onSelect(r);
        }}
      />
    </Box>
  );
}

// ---- Rail content (DNKADM-0298 U1/U2) -----------------------------------------

/** The walk-in panel's mode-aware presentational strings. */
function walkInHeadline(walkIn: WalkInAvailability): string {
  if (walkIn.status === 'unknown') return 'Operating hours unavailable.';
  if (walkIn.status === 'closed') return 'Outside operating hours.';
  if (walkIn.bays.length === 0) return 'No bays on the roster.';
  if (walkIn.freeNow === 0) return 'No bays free right now';
  return `${walkIn.freeNow} of ${walkIn.bays.length} ${walkIn.bays.length === 1 ? 'bay' : 'bays'} free now`;
}

/** One bay's status + until strings for the panel rows. */
function bayLine(
  bay: WalkInAvailability['bays'][number],
  timeZone: string | null,
): { status: string; until: string } {
  if (!bay.free) {
    return {
      status: 'In use',
      until: bay.busyUntil ? `frees ${timeLabel(bay.busyUntil, timeZone)}` : '',
    };
  }
  if (bay.openAllDay) return { status: 'Open all day', until: '' };
  return {
    status: 'Free now',
    until: bay.freeUntil ? `until ${timeLabel(bay.freeUntil, timeZone)}` : 'until close',
  };
}

/** Shared bar-split colors: vivid = still to come, muted = done. */
function loadColors(t: AdminTokens): { toCome: string; done: string; track: string } {
  return { toCome: t.success, done: t.successSoft, track: t.raise };
}

/** Accessible description for one coach's bar (chart read-out for AT + tests). */
function coachBarLabel(c: CoachLoadRow): string {
  return `${c.label}: ${c.total} coach-required ${c.total === 1 ? 'booking' : 'bookings'}, ${c.done} done, ${c.toCome} still to come`;
}

const RAIL_TITLE_SX = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
} as const;

/** The md+ side rail: coach load (vertical bars), walk-in, day counters. */
function SideRail({
  coaches,
  walkIn,
  progress,
  timeZone,
  onCoachTap,
  onBayTap,
}: {
  coaches: CoachLoadRow[];
  walkIn: WalkInAvailability;
  progress: { done: number; toCome: number };
  timeZone: string | null;
  onCoachTap: (coach: CoachLoadRow) => void;
  onBayTap: (bay: { id: string; label: string }) => void;
}) {
  const t = resolveTokens(useTheme());
  const colors = loadColors(t);
  const maxTotal = Math.max(1, ...coaches.map((c) => c.total));
  return (
    <Stack
      data-testid="now-next-rail"
      spacing={1.75}
      sx={{ display: { xs: 'none', md: 'flex' } }}
    >
      {coaches.length > 0 && (
        <Paper elevation={1} sx={{ p: 2 }} component="section" aria-label="Coach load today">
          <Typography sx={{ ...RAIL_TITLE_SX, color: t.textSubtle }}>Coach load today</Typography>
          <Typography sx={{ fontSize: 12, color: t.textMuted, mt: 0.25 }}>
            Coach-required bookings
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${coaches.length}, 1fr)`,
              gap: 1.5,
              alignItems: 'end',
              height: 132,
              mt: 2,
            }}
          >
            {coaches.map((c) => (
              // A real button (tap, never hover — DNKADM-0300 F): opens the
              // coach's day-bookings popout. The chart graphic inside keeps
              // its own accessible read-out.
              <Box
                key={c.id}
                component="button"
                type="button"
                onClick={() => onCoachTap(c)}
                aria-label={`${c.label}: view bookings`}
                sx={{
                  font: 'inherit',
                  cursor: 'pointer',
                  border: 0,
                  bgcolor: 'transparent',
                  p: 0,
                  height: '100%',
                  display: 'flex',
                }}
              >
              <Stack
                role="img"
                aria-label={coachBarLabel(c)}
                sx={{ alignItems: 'center', gap: 0.75, height: '100%', width: '100%', justifyContent: 'flex-end' }}
              >
                <Typography variant="mono" sx={{ fontSize: 14, fontWeight: 600 }}>
                  {c.total}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 52,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    height: `${Math.round((c.total / maxTotal) * 100)}%`,
                    minHeight: 3,
                    borderRadius: '6px 6px 0 0',
                    overflow: 'hidden',
                    bgcolor: colors.track,
                  }}
                >
                  {c.total > 0 && (
                    <>
                      <Box sx={{ height: `${(c.toCome / c.total) * 100}%`, bgcolor: colors.toCome }} />
                      <Box sx={{ height: `${(c.done / c.total) * 100}%`, bgcolor: colors.done }} />
                    </>
                  )}
                </Box>
              </Stack>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${coaches.length}, 1fr)`,
              gap: 1.5,
              mt: 1.25,
              pt: 1.25,
              borderTop: `1px solid ${t.lineSoft}`,
            }}
          >
            {coaches.map((c) => (
              <Box key={c.id} sx={{ textAlign: 'center', minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
                  {c.firstName}
                </Typography>
                <Typography sx={{ fontSize: 11, color: t.textMuted, mt: 0.25 }}>
                  {c.toCome} to come
                </Typography>
              </Box>
            ))}
          </Box>
          <Stack direction="row" sx={{ gap: 1.75, mt: 1.5 }}>
            <LegendSwatch color={colors.toCome} label="Still to come" />
            <LegendSwatch color={colors.done} label="Done" />
          </Stack>
        </Paper>
      )}

      <Paper elevation={1} sx={{ p: 2 }} component="section" aria-label="Room for a walk-in">
        <Typography sx={{ ...RAIL_TITLE_SX, color: t.textSubtle }}>Room for a walk-in</Typography>
        <Typography sx={{ fontSize: 17, fontWeight: 600, mt: 1, color: t.successSoftInk }}>
          {walkInHeadline(walkIn)}
        </Typography>
        {walkIn.status === 'ok' && walkIn.bays.length > 0 && (
          <>
            <Stack sx={{ mt: 1.5 }}>
              {walkIn.bays.map((bay) => {
                const line = bayLine(bay, timeZone);
                return (
                  // A real button (tap, never hover — DNKADM-0300 F): opens
                  // the bay's day-bookings popout.
                  <Box
                    key={bay.id}
                    component="button"
                    type="button"
                    onClick={() => onBayTap(bay)}
                    aria-label={`${bay.label}: view bookings`}
                    sx={{
                      font: 'inherit',
                      cursor: 'pointer',
                      border: 0,
                      bgcolor: 'transparent',
                      textAlign: 'left',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      minHeight: 44,
                      pt: 1.1,
                      mt: 1.1,
                      px: 0,
                      borderTop: `1px solid ${t.lineFaint}`,
                      '&:first-of-type': { borderTop: 0, mt: 0, pt: 0 },
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 44 }} noWrap>
                      {bay.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: t.text2, flex: 1, minWidth: 0 }} noWrap>
                      {line.status}
                    </Typography>
                    {line.until && (
                      <Typography variant="mono" sx={{ fontSize: 12, color: t.successSoftInk }}>
                        {line.until}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
            <Typography sx={{ fontSize: 11, color: t.textMuted, mt: 1.5 }}>
              Openings from now to close. Pending requests count as booked.
            </Typography>
          </>
        )}
      </Paper>

      <Paper elevation={1} sx={{ p: 2, display: 'flex', gap: 3.25 }}>
        <DayCounter value={progress.done} label="Done" />
        <DayCounter value={progress.toCome} label="To come" />
      </Paper>
    </Stack>
  );
}

/**
 * The <md compact strip — collapse steps 1+2 (the design, exactly): the rail's
 * three facts as one strip under the header, coach load flipped to HORIZONTAL
 * bars. Rendered above the sections; hidden at md+ where the side rail shows.
 */
function CompactRailStrip({
  coaches,
  walkIn,
  progress,
  timeZone,
  onCoachTap,
}: {
  coaches: CoachLoadRow[];
  walkIn: WalkInAvailability;
  progress: { done: number; toCome: number };
  timeZone: string | null;
  onCoachTap: (coach: CoachLoadRow) => void;
}) {
  const t = resolveTokens(useTheme());
  const colors = loadColors(t);
  const bestFree = walkIn.status === 'ok' ? walkIn.bays.filter((b) => b.free) : [];
  const openAllDay = bestFree.filter((b) => b.openAllDay).length;
  const detail =
    walkIn.status === 'ok' && bestFree.length > 0
      ? openAllDay === bestFree.length
        ? 'open all day'
        : bayLine(bestFree[0] as WalkInAvailability['bays'][number], timeZone).until
      : '';
  return (
    <Paper
      data-testid="now-next-strip"
      elevation={1}
      sx={{
        p: 2,
        display: { xs: 'flex', md: 'none' },
        alignItems: 'stretch',
        gap: 2,
      }}
    >
      <Box sx={{ flex: 1.1, minWidth: 0 }}>
        <Typography sx={{ ...RAIL_TITLE_SX, color: t.textSubtle }}>Room for a walk-in</Typography>
        <Typography
          sx={{ fontSize: 16, fontWeight: 600, mt: 0.75, color: t.successSoftInk, lineHeight: 1.25 }}
        >
          {walkInHeadline(walkIn)}
        </Typography>
        {detail && (
          <Typography variant="mono" sx={{ fontSize: 12, color: t.text2, mt: 0.5 }}>
            {detail}
          </Typography>
        )}
      </Box>
      {coaches.length > 0 && (
        <>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...RAIL_TITLE_SX, color: t.textSubtle }}>Coach load today</Typography>
            <Stack sx={{ gap: 0.9, mt: 1.1 }}>
              {coaches.map((c) => (
                // A real button (tap, never hover): the same coach popout as
                // the side rail's vertical bars (DNKADM-0300 F).
                <Box
                  key={c.id}
                  component="button"
                  type="button"
                  onClick={() => onCoachTap(c)}
                  aria-label={`${c.label}: view bookings`}
                  sx={{
                    font: 'inherit',
                    cursor: 'pointer',
                    border: 0,
                    bgcolor: 'transparent',
                    p: 0,
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                <Stack
                  direction="row"
                  role="img"
                  aria-label={coachBarLabel(c)}
                  sx={{ alignItems: 'center', gap: 1.1 }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 600, width: 52, flexShrink: 0 }} noWrap>
                    {c.firstName}
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: 10,
                      borderRadius: 5,
                      bgcolor: colors.track,
                      overflow: 'hidden',
                      display: 'flex',
                    }}
                  >
                    {c.total > 0 && (
                      <>
                        <Box sx={{ width: `${(c.done / c.total) * 100}%`, bgcolor: colors.done }} />
                        <Box
                          sx={{ width: `${(c.toCome / c.total) * 100}%`, bgcolor: colors.toCome }}
                        />
                      </>
                    )}
                  </Box>
                  <Typography
                    variant="mono"
                    sx={{ fontSize: 12, fontWeight: 600, width: 16, textAlign: 'right', flexShrink: 0 }}
                  >
                    {c.total}
                  </Typography>
                </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        </>
      )}
      <Divider orientation="vertical" flexItem />
      <Stack direction="row" sx={{ gap: 2.25, alignItems: 'flex-start', flexShrink: 0 }}>
        <DayCounter value={progress.done} label="Done" compact />
        <DayCounter value={progress.toCome} label="To come" compact />
      </Stack>
    </Paper>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  const t = resolveTokens(useTheme());
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 9, height: 9, borderRadius: 0.5, bgcolor: color }} />
      <Typography sx={{ fontSize: 11, color: t.textMuted }}>{label}</Typography>
    </Stack>
  );
}

function DayCounter({ value, label, compact }: { value: number; label: string; compact?: boolean }) {
  const t = resolveTokens(useTheme());
  return (
    <Box>
      <Typography variant="mono" sx={{ fontSize: compact ? 20 : 22, fontWeight: 600 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11, color: t.textMuted, mt: 0.25 }}>{label}</Typography>
    </Box>
  );
}

// ---- Sections -----------------------------------------------------------------

function BoardSection({
  label,
  dotColor,
  empty,
  count,
  headerExtra,
  children,
}: {
  label: string;
  dotColor: string;
  empty: string;
  count: number;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}) {
  const t = resolveTokens(useTheme());
  return (
    <Paper elevation={1} sx={{ p: 2 }} component="section" aria-label={label}>
      {/* DNKADM-0343 U2: the header WRAPS. It was a single non-wrapping `space-between` row,
          and `headerExtra` is a deliberately `nowrap` link — so once title + count + link
          exceeded the card's width the link ran straight past the Paper's edge ("All pending
          reques…" clipped at the border). Wrapping drops the link onto its own line INSIDE
          the card; it stays `nowrap` so it is never a clipped word. */}
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor }} />
          <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>
            {label}
          </Typography>
          <Chip size="small" label={count} sx={{ height: 20, fontSize: 11.5, fontWeight: 600 }} />
        </Stack>
        {headerExtra}
      </Stack>
      {count === 0 ? (
        <Typography variant="body2" sx={{ mt: 1.25, color: t.text3 }}>
          {empty}
        </Typography>
      ) : (
        <Stack spacing={1.25} sx={{ mt: 1.25 }}>
          {children}
        </Stack>
      )}
    </Paper>
  );
}

/**
 * The unresolved-queue row's payment-state chip (DNKADM-0350 U1/U2) — the
 * SAME states/colors/text-token discipline as the Day-grid block badge, via
 * the same `derivePaymentState`/`paymentStateLabel` pure functions so the two
 * surfaces can never drift on the same reservation. `aria-hidden`: the
 * card's own aria-label carries the same text.
 */
function PaymentStateChip({
  reservation,
  state,
}: {
  reservation: Reservation;
  state: PaymentState;
}) {
  const cm = resolveCalendarMaps(useTheme());
  const pair = cm.paymentState[state];
  return (
    <Box
      aria-hidden
      sx={{
        px: '6px',
        py: '1px',
        borderRadius: '999px',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        bgcolor: pair.bg,
        color: pair.fg,
      }}
    >
      {paymentBadgeText(reservation, state)}
    </Box>
  );
}

function ReservationCard({
  reservation,
  timeZone,
  now,
  onSelect,
  actions,
  paymentState,
}: {
  reservation: Reservation;
  timeZone: string | null;
  now: Date;
  onSelect: (r: Reservation) => void;
  actions: React.ReactNode;
  /**
   * The payment-state chip (DNKADM-0350 U1/U2) — opt-in, so a card that passes
   * nothing renders byte-identical to before. The Unresolved queue passes the
   * booking's full derived state; the "Arriving now & soon" and "Needs
   * confirmation" cards pass ONLY the two states the desk must act on at the
   * counter (DNKADM-0402 U5): `'unpaid'`, and `'in-progress'` — a payment
   * pay-api never confirmed (Operator decision, 2026-09-22) — plus
   * `'needs-review'` (DNKADM-0408 U1), which outranks both, as it does in
   * `derivePaymentState`.
   */
  paymentState?: PaymentState | null;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const color = deriveServiceColor(
    reservation.serviceName,
    theme.palette.mode === 'dark' ? 'dark' : 'light',
  );
  const startsIn = minutesUntil(reservation.startTime, now);
  // DNKADM-0454 V-67: once the end time has passed the booking is not "in progress", it is a
  // started booking nobody closed out: say the day it started.
  const endMs = reservation.endTime ? Date.parse(reservation.endTime) : Number.NaN;
  const startMs = reservation.startTime ? Date.parse(reservation.startTime) : Number.NaN;
  const endedUnclosed =
    startsIn !== null && startsIn <= 0 && Number.isFinite(endMs) && endMs <= now.getTime();
  const startedLabel =
    endedUnclosed && Number.isFinite(startMs)
      ? `Started ${dateLabel(zonedDateOf(startMs, timeZone)) ?? 'earlier'}`
      : endedUnclosed
        ? 'Not closed out'
        : null;
  const when =
    startedLabel !== null
      ? startedLabel
      : startsIn !== null && startsIn <= 0
      ? 'In progress'
      : startsIn !== null && startsIn <= SOON_WINDOW_MINUTES
        ? `In ${startsIn} min`
        : timeLabel(reservation.startTime, timeZone);

  return (
    <Stack
      role="button"
      tabIndex={0}
      aria-label={[
        `Open ${reservation.contactName ?? 'reservation'} ${timeLabel(reservation.startTime, timeZone)}`,
        // The payment chip is `aria-hidden` (a visual duplicate) — its text
        // rides in this accessible name instead (DNKADM-0350 U1).
        paymentState ? paymentBadgeText(reservation, paymentState) : null,
      ]
        .filter(Boolean)
        .join(', ')}
      onClick={() => onSelect(reservation)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(reservation);
        }
      }}
      sx={{
        gap: 0.75,
        p: 1.75,
        borderRadius: 3,
        border: `1px solid ${t.lineSoft}`,
        borderLeft: `4px solid ${color.main}`,
        cursor: 'pointer',
        // Decoration only — nothing DEPENDS on hover (DNKADM-0298 U3); the
        // active state gives the same feedback on touch.
        '&:hover': { bgcolor: t.raise },
        '&:active': { bgcolor: t.raise },
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        <Typography variant="mono" sx={{ fontSize: 13.5, color: t.text2 }}>
          {timeLabel(reservation.startTime, timeZone)}
        </Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: t.successSoftInk }}>
          {when}
        </Typography>
        {/* Payment-state chip (DNKADM-0350 U1) — `aria-hidden`, its text
            already rides in the card's own aria-label above. */}
        {paymentState && <PaymentStateChip reservation={reservation} state={paymentState} />}
      </Stack>
      <Typography sx={{ fontSize: 19, fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.2 }} noWrap>
        <MemberNameLink
          name={reservation.contactName}
          contactId={reservation.contactId}
          fallback="Reservation"
        />
      </Typography>
      <Typography sx={{ fontSize: 13, color: t.text2 }} noWrap>
        {[serviceWithCoach(reservation), reservation.assignedResourceName]
          .filter(Boolean)
          .join(' · ')}
      </Typography>
      {/* The action row is omitted entirely when there are no actions
          (DNKADM-0361 — a STAFF card), rather than laying out an empty
          gapped Stack under the card's meta line. */}
      {actions && (
        <Stack direction="row" sx={{ gap: 1.25, mt: 0.5 }}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * "Later today" one-line row (DNKADM-0298 U3, collapse step 3): the only group
 * with no actions, so it renders as a single 56px row in every orientation.
 */
function LaterRow({
  reservation,
  timeZone,
  onSelect,
}: {
  reservation: Reservation;
  timeZone: string | null;
  onSelect: (r: Reservation) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const color = deriveServiceColor(
    reservation.serviceName,
    theme.palette.mode === 'dark' ? 'dark' : 'light',
  );
  return (
    <Stack
      direction="row"
      role="button"
      tabIndex={0}
      aria-label={`Open ${reservation.contactName ?? 'reservation'} ${timeLabel(reservation.startTime, timeZone)}`}
      onClick={() => onSelect(reservation)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(reservation);
        }
      }}
      sx={{
        alignItems: 'center',
        gap: 1.75,
        px: 2,
        minHeight: 56,
        borderRadius: 2.5,
        border: `1px solid ${t.lineFaint}`,
        borderLeft: `4px solid ${color.main}`,
        cursor: 'pointer',
        '&:hover': { bgcolor: t.raise },
        '&:active': { bgcolor: t.raise },
      }}
    >
      <Typography variant="mono" sx={{ fontSize: 13.5, color: t.text2, width: 72, flexShrink: 0 }}>
        {timeLabel(reservation.startTime, timeZone)}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
        <MemberNameLink
          name={reservation.contactName}
          contactId={reservation.contactId}
          fallback="Reservation"
        />
      </Typography>
      <Typography sx={{ fontSize: 13, color: t.textMuted, display: { xs: 'none', sm: 'block' } }} noWrap>
        {serviceWithCoach(reservation)}
      </Typography>
      <Chip
        size="small"
        label={isCheckedIn(reservation) ? 'Checked in' : 'Confirmed'}
        sx={{ bgcolor: t.successSoft, color: t.successSoftInk, fontWeight: 500, flexShrink: 0 }}
      />
    </Stack>
  );
}
